import { NODE_REWARD_ABI } from '@lfg/abis';
import { useBoolean } from '@lfg/core/hooks';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { waitForTransactionReceipt, writeContract } from 'wagmi/actions';

import TransactionSubmittedToast from '@/components/TransactionSubmittedToast';
import { NODE_REWARD_V1_SC_ADDRESS } from '@/global/configs/common.config';
import { useGA, useWeb3 } from '@/hooks';
import { useWeb3Balance } from '@/providers';
import { useClaimRewardV1Mutation, useFetchRewardStatsQuery } from '@/store/services/reward.service';
import { useAppSelector } from '@/store/utils';
import { logError } from '@/utils/helpers';

import { ClaimRewardProps, RewardV1Hook } from '../types/reward.type';

const useRewardV1 = (): RewardV1Hook => {
  const { config, address } = useWeb3();
  const { trackButtonClickEvent } = useGA();
  const { getWalletBalance } = useWeb3Balance();
  const { nodeId: nft } = useAppSelector(state => state.app);
  const { projectInfo } = useAppSelector(state => state.project);
  const [claimRewardV1] = useClaimRewardV1Mutation();
  const {
    isLoading: isLoadingRewardStats,
    data: rewardStats,
    refetch: refetchRewardStats,
  } = useFetchRewardStatsQuery({ address: address as string, nft, page: 'reward' }, { skip: !address || !nft });

  const { value: isClaiming, setTrue: setIsClaimingTrue, setFalse: setIsClaimingFalse } = useBoolean(false);

  const handleClaimReward = useCallback(
    async ({ nodeId, onSuccess }: ClaimRewardProps) => {
      if (!address || !projectInfo?.nft) return;
      setIsClaimingTrue();
      trackButtonClickEvent({ name: 'claim_rewards', address });
      try {
        const nftAddress = nodeId || projectInfo.nft;
        const claimRes = await claimRewardV1({ nft: nftAddress, address });
        if (
          !claimRes.data ||
          claimRes.data.amount === 0 ||
          !claimRes.data.contract ||
          !claimRes.data.deadline ||
          Object.keys(claimRes.data.signature).length === 0
        ) {
          toast.error('Claim reward failed. Please try again or contact to support');
          return;
        }
        const {
          amount,
          deadline,
          nonce,
          signature: { v, s, r },
        } = claimRes.data;
        const receiver = address;
        const hash = await writeContract(config, {
          abi: NODE_REWARD_ABI,
          address: NODE_REWARD_V1_SC_ADDRESS,
          functionName: 'claim',
          args: [receiver, amount, deadline, nonce, v, r, s],
        });
        toast(TransactionSubmittedToast());
        await waitForTransactionReceipt(config, { hash });
        toast.success('Claimed rewards successfully');
        getWalletBalance();
        refetchRewardStats();
        onSuccess?.({ txHash: hash });
      } catch (error: unknown) {
        const errorDetails = logError(error, 'handleReward');
        const errorMessage = errorDetails.message || (error instanceof Error ? error.message : 'Claim reward failed');
        toast.error(errorMessage);
        throw error;
      } finally {
        setIsClaimingFalse();
      }
    },
    [
      address,
      config,
      nft,
      projectInfo,
      claimRewardV1,
      getWalletBalance,
      setIsClaimingFalse,
      setIsClaimingTrue,
      refetchRewardStats,
    ],
  );

  return {
    rewardStats,
    isClaiming,
    isLoadingRewardStats,
    handleClaimReward,
    refetchRewardStats,
  };
};

export { useRewardV1 };
